import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  TextField,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, Favorite, FavoriteBorder } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useDesign } from '../context/DesignContext';
import { useWishlist } from '../context/WishlistContext';
import { db } from '../firebase';
import { collection, onSnapshot } from 'firebase/firestore';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  category: string; // nombre de la categoría para filtrado/visualización
}

interface AdminProductDoc {
  name?: string;
  description?: string;
  price?: number;
  categoryId?: string | null;
  imageUrl?: string;
  images?: string[];
}

interface CategoryDoc {
  id: string;
  name: string;
  image: string;
}

// Productos ahora se cargan desde Firestore (collection 'products')

// Categorías dinámicas desde Firestore administradas por el panel
// Se inicializa con "Todos" y se actualiza en tiempo real

const CatalogPage = () => {
  const { config } = useDesign();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const { addItem } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const [firestoreCategories, setFirestoreCategories] = useState<CategoryDoc[]>([]);
  
  const categories = React.useMemo(() => {
    const allCat = { 
      id: 'all', 
      name: 'Todos', 
      image: config.allCategoryImageUrl || 'https://images.unsplash.com/photo-1490750967868-58cb75069faf?w=150' 
    };
    return [allCat, ...firestoreCategories];
  }, [config.allCategoryImageUrl, firestoreCategories]);

  const scrollContainerRef = useRef<HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const scrollCategories = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const { current } = scrollContainerRef;
      const scrollAmount = direction === 'left' ? -300 : 300;
      current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Suscribir categorías del panel administrador
  useEffect(() => {
    if (!db) return;
    const unsub = onSnapshot(collection(db, 'categories'), (snap) => {
      const cats: CategoryDoc[] = snap.docs.map((d) => {
        const data = d.data() as any;
        const name = typeof data.name === 'string' && data.name.trim() ? data.name.trim() : d.id;
        return { 
          id: d.id, 
          name,
          image: data.image || data.imageUrl || 'https://via.placeholder.com/150',
          order: data.order
        } as any;
      });
      cats.sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
      setFirestoreCategories(cats);
    }, (err) => {
      console.warn('[Catalog] Error suscribiendo categorías', err);
      setFirestoreCategories([]);
    });
    return () => unsub();
  }, []);

  // Suscribir productos desde Firestore
  useEffect(() => {
    if (!db) return;
    const unsub = onSnapshot(collection(db, 'products'), (snap) => {
      // Mapa id -> nombre de categoría para asignar nombre legible al producto
      const catMap = new Map(categories.map((c) => [c.id, c.name]));
      const prods: Product[] = [];
      snap.docs.forEach((d) => {
        const data = d.data() as any; // Cast to any to access 'visible'
        // Filtrar si visible es false (default true)
        if (data.visible === false) return;

        const image = data.imageUrl || (Array.isArray(data.images) && data.images.length ? data.images[0] : 'https://via.placeholder.com/400x250?text=Producto');
        // Usamos el ID de la categoría directamente para el filtrado, pero guardamos el nombre si se necesita mostrar
        const categoryId = data.categoryId ? data.categoryId.toString() : '';
        const categoryName = categoryId ? (catMap.get(categoryId) || 'Sin categoría') : 'Sin categoría';
        
        prods.push({
          id: d.id,
          name: data.name || d.id,
          description: data.description || '',
          price: typeof data.price === 'number' ? data.price : 0,
          image,
          category: categoryId, // Usamos ID para filtrar
        });
      });
      setProducts(prods);
    }, (err) => {
      console.warn('[Catalog] Error suscribiendo productos', err);
      setProducts([]);
    });
    return () => unsub();
  }, [categories]);

  const filterProducts = useCallback(() => {
    let filtered = [...products];

    if (selectedCategoryId !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategoryId);
    }

    if (searchQuery) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort products
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'name':
        default:
          return a.name.localeCompare(b.name);
      }
    });

    setFilteredProducts(filtered);
  }, [products, searchQuery, selectedCategoryId, sortBy]);

  useEffect(() => {
    filterProducts();
  }, [filterProducts]);

  const handleAddToCart = (product: Product) => {
    addItem(product, 1);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom sx={{ color: '#333', fontWeight: 'bold', fontFamily: '"Playfair Display", serif', mb: 4 }}>
        Catálogo de Productos
      </Typography>

      {/* Filters */}
      <Box sx={{ mb: 6 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Buscar productos..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ borderRadius: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Ordenar por</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Ordenar por"
              >
                <MenuItem value="name">Nombre</MenuItem>
                <MenuItem value="price-low">Precio: Menor a Mayor</MenuItem>
                <MenuItem value="price-high">Precio: Mayor a Menor</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Circular Categories List */}
      <Box sx={{ position: 'relative', mb: 6 }}>
        {!isMobile && (
          <IconButton
            onClick={() => scrollCategories('left')}
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: 'rgba(255,255,255,0.8)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
              boxShadow: 2,
              display: { xs: 'none', md: 'flex' }
            }}
          >
            <ArrowBackIos />
          </IconButton>
        )}

        <Box
          ref={scrollContainerRef}
          sx={{
            display: 'flex',
            overflowX: 'auto',
            py: 2,
            px: { xs: 2, md: 6 },
            gap: 4,
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            scrollBehavior: 'smooth'
          }}
        >
          {categories.map((cat) => (
            <Box
              key={cat.id}
              onClick={() => setSelectedCategoryId(cat.id)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                minWidth: { xs: 100, md: 150 },
                transition: 'transform 0.2s',
                opacity: selectedCategoryId === cat.id ? 1 : 0.7,
                '&:hover': { transform: 'scale(1.05)', opacity: 1 }
              }}
            >
              <Box
                sx={{
                  width: { xs: 80, md: 120 },
                  height: { xs: 80, md: 120 },
                  borderRadius: '50%',
                  overflow: 'hidden',
                  mb: 2,
                  boxShadow: selectedCategoryId === cat.id 
                    ? '0 0 0 3px #e91e63, 0 4px 12px rgba(0,0,0,0.2)' 
                    : '0 4px 12px rgba(0,0,0,0.1)',
                  border: '2px solid transparent',
                  transition: 'all 0.3s ease',
                }}
              >
                <Box
                  component="img"
                  src={cat.image}
                  alt={cat.name}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{ 
                  color: selectedCategoryId === cat.id ? 'primary.main' : '#555', 
                  fontWeight: selectedCategoryId === cat.id ? 700 : 500,
                  textAlign: 'center',
                  fontSize: { xs: '0.9rem', md: '1.1rem' },
                  whiteSpace: 'normal',
                  lineHeight: 1.2,
                  maxWidth: '100%'
                }}
              >
                {cat.name}
              </Typography>
            </Box>
          ))}
        </Box>

        {!isMobile && (
          <IconButton
            onClick={() => scrollCategories('right')}
            sx={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: 'rgba(255,255,255,0.8)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
              boxShadow: 2,
              display: { xs: 'none', md: 'flex' }
            }}
          >
            <ArrowForwardIos />
          </IconButton>
        )}
      </Box>

      {/* Products Grid */}
      <Grid container spacing={4}>
        {filteredProducts.map((product) => (
          <Grid item xs={12} sm={6} md={4} key={product.id}>
            <Card
              elevation={2}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#fff',
                borderRadius: 3,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 12px 20px rgba(0,0,0,0.1)'
                }
              }}
            >
              <Box sx={{ position: 'relative', paddingTop: '100%' }}>
                <CardMedia
                  component="img"
                  image={product.image}
                  alt={product.name}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    toggleWishlist(product.id);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 2,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                    color: isInWishlist(product.id) ? '#E91E63' : 'action.active',
                  }}
                >
                  {isInWishlist(product.id) ? <Favorite /> : <FavoriteBorder />}
                </IconButton>
              </Box>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                <Typography variant="h6" component="h3" sx={{ mb: 1, color: '#333', fontWeight: 600, fontFamily: '"Playfair Display", serif' }}>
                  {product.name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#666', flexGrow: 1, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {product.description}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(product.price)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    component={Link}
                    to={`/product/${product.id}`}
                    fullWidth
                    sx={{
                      borderRadius: '50px',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        borderColor: 'primary.dark',
                        backgroundColor: 'rgba(233, 30, 99, 0.04)'
                      }
                    }}
                  >
                    Ver
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handleAddToCart(product)}
                    fullWidth
                    sx={{
                      borderRadius: '50px',
                      textTransform: 'none',
                      fontWeight: 600,
                      backgroundColor: 'primary.main',
                      boxShadow: '0 4px 12px rgba(233, 30, 99, 0.3)',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                        boxShadow: '0 6px 15px rgba(233, 30, 99, 0.4)'
                      }
                    }}
                  >
                    Agregar
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {filteredProducts.length === 0 && (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" sx={{ color: '#666' }}>
            No se encontraron productos que coincidan con tu búsqueda.
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default CatalogPage;